import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { parseProseRelations, resolveProseRelations, drawProseRelation } from './proseRelations';

// The confirm step for relations written in prose (M-3: confirm-before-draw).
//
// Renders one chip per `name::[[target]]` found in the saved text. Nothing here
// touches the graph until a person presses draw — parsing produces proposals,
// and the owner's reason for wanting the step is that it cuts erroneous
// relations before they exist rather than after.
//
// The three states are deliberately different shapes, not three colours of the
// same row: a refusal has no draw button at all, because there is nothing to
// confirm; an already-drawn mention states that it is already drawn, which is
// what makes re-saving the same text safe to do repeatedly.

export default function ProseRelationChips({ sourceId, text, existing, onDrawn }) {
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // What THIS session drew. Drawing triggers a refetch, which re-resolves every
  // proposal — and the edge now exists, so the row would come back as the muted
  // "already drawn", replacing the confirmation with something that reads like
  // nothing happened. Remembering our own writes keeps the acknowledgement
  // (and the "+1 cast", which is the part a user needs to see) on screen.
  const drawnHere = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    const mentions = parseProseRelations(text);
    if (mentions.length === 0) {
      setProposals([]);
      return;
    }
    resolveProseRelations(sourceId, mentions, existing).then((rows) => {
      if (cancelled) return;
      setProposals(
        rows.map((r) =>
          r.status === 'already' && drawnHere.current.has(`${r.resolvedName}|${r.target}`)
            ? { ...r, status: 'drawn' }
            : r
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [sourceId, text, existing]);

  if (proposals.length === 0) return null;

  const draw = async (p, i) => {
    setBusy(i);
    setError(null);
    try {
      await drawProseRelation(sourceId, p);
      drawnHere.current.add(`${p.resolvedName}|${p.target}`);
      setProposals((cur) => cur.map((x, j) => (j === i ? { ...x, status: 'drawn' } : x)));
      if (onDrawn) onDrawn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="wv-prose-rels">
      <div className="cap-label">connections written in this text</div>
      {proposals.map((p, i) => (
        <div key={`${p.name}-${p.target}`} className="wv-prose-rel">
          {p.status === 'refused' ? (
            <span className="wv-prose-refused">
              “{p.name}” is not a relation anyone has named yet — nothing was drawn. Rephrase it, or
              mint the type deliberately on <Link to="/triage">the pair page</Link>.
            </span>
          ) : p.status === 'drawn' ? (
            <span className="wv-prose-drawn">
              ✓ drawn: <b>{p.resolvedName.replace(/_/g, ' ')}</b> → «{p.display}» · your +1 cast
            </span>
          ) : p.status === 'already' ? (
            <span className="trust-muted">
              <b>{p.resolvedName.replace(/_/g, ' ')}</b> → «{p.display}» — already drawn
            </span>
          ) : (
            <>
              <span style={{ flex: 1, minWidth: 200 }}>
                draw <b>{p.resolvedName.replace(/_/g, ' ')}</b> → «{p.display}»
                {p.viaSynonym && <span className="wv-prose-syn">from “{p.viaSynonym}”</span>}
              </span>
              <button className="wv-prose-draw" disabled={busy === i} onClick={() => draw(p, i)}>
                {busy === i ? 'drawing…' : '✓ draw'}
              </button>
              <button
                className="cap-cancel"
                onClick={() => setProposals((cur) => cur.filter((_, j) => j !== i))}
              >
                skip
              </button>
            </>
          )}
        </div>
      ))}
      {error && <div className="cap-error">{error}</div>}
    </div>
  );
}
